import { produce } from 'immer';
import { pick, isNil } from 'lodash/fp';
import type { ToBeTyped } from 'app/globalTypes';
import type { TypedReduxActionOutcomes } from 'app/store/storeTypes';
import {
    type ViewDeleted,
    ExecutionTriggerType,
    type ScheduleDrivenTriggerResponse,
    type DataDrivenTriggerResponse,
    ACCESSIBLE,
    DEPLOYMENT_DETAILS,
    NOT_ACCESSIBLE,
    type Accessible,
    type NotAccessible,
    type DeploymentDetails
} from 'app/lib/types/server';
import { toStringFqn } from 'app/lib/utils';
import { RequestStatus } from 'app/lib/constants';
import { DELETE_VIEW } from 'app/store/entities/types.js';
import { PAUSE_SNOWFLAKE_OUTPUT, RESUME_SNOWFLAKE_OUTPUT } from 'app/store/deployView/types.js';
import { VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE } from './types.js';

export type AccessStateSlice = {
    validationsBySourceFqn: {
        values: Record<string, Accessible | NotAccessible | DeploymentDetails>;
        statuses: Record<string, RequestStatus>;
    };
};

function deleteEntity(draft: AccessStateSlice, entityId: string, fqn: string | null) {
    if (fqn) {
        delete draft.validationsBySourceFqn.values[fqn];
        delete draft.validationsBySourceFqn.statuses[fqn];
    }
}

export const INITIAL_STATE: AccessStateSlice = {
    validationsBySourceFqn: {
        values: {},
        statuses: {}
    }
};

function toggleTrigger(draft: AccessStateSlice, fqn: string, value: boolean) {
    const validation = draft.validationsBySourceFqn.values[fqn];

    if (!validation) {
        return;
    }

    if ('trigger' in validation) {
        const { trigger } = validation;
        if (trigger.triggerType === ExecutionTriggerType.SCHEDULE_DRIVEN) {
            (trigger as ScheduleDrivenTriggerResponse).enabled = value;
        }
        if (trigger.triggerType === ExecutionTriggerType.DATA_DRIVEN) {
            (trigger as DataDrivenTriggerResponse).enabled = value;
        }
    }
}

export const accessReducer = produce((draft, action: TypedReduxActionOutcomes) => {
    switch (action.type) {
        case VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE.STARTED: {
            for (const object of action.payload.objects) {
                const fqn = toStringFqn(object);
                if (fqn) {
                    draft.validationsBySourceFqn.statuses[fqn] = RequestStatus.PENDING;
                }
            }
            break;
        }

        case VALIDATE_SNOWFLAKE_ACCESS_ACTION_TYPE.SUCCEEDED: {
            const { tables } = action.payload;

            for (const table of tables) {
                if (
                    '_kind' in table &&
                    (table._kind === ACCESSIBLE ||
                        table._kind === DEPLOYMENT_DETAILS ||
                        table._kind === NOT_ACCESSIBLE)
                ) {
                    if ('database' in table && 'schema' in table && 'table' in table) {
                        const fqnObject = pick(['database', 'schema', 'table'], table);
                        const fqn = toStringFqn(fqnObject);

                        if (fqn) {
                            draft.validationsBySourceFqn.values[fqn] = table;
                            draft.validationsBySourceFqn.statuses[fqn] = RequestStatus.RESOLVED;
                        }
                    }
                }
            }

            break;
        }

        // @ts-expect-error [TODO] type dataset data store slice
        case DELETE_VIEW.SUCCEEDED: {
            const { payload } = action as ToBeTyped;
            const { viewRef, tableFqn } = <ViewDeleted>payload;
            const { entityId } = viewRef;

            if (!isNil(tableFqn)) {
                deleteEntity(draft, entityId as unknown as string, toStringFqn(tableFqn));
            }

            break;
        }

        // @ts-expect-error [TODO] type dataset data store slice
        case PAUSE_SNOWFLAKE_OUTPUT.SUCCEEDED: {
            const { params } = action as ToBeTyped;
            const fqn = toStringFqn(params);
            toggleTrigger(draft, fqn, false);
            break;
        }

        // @ts-expect-error [TODO] type dataset data store slice
        case RESUME_SNOWFLAKE_OUTPUT.SUCCEEDED: {
            const { params } = action as ToBeTyped;
            const fqn = toStringFqn(params);
            toggleTrigger(draft, fqn, true);
            break;
        }

        default:
            break;
    }
}, INITIAL_STATE);
